import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

export const qk = {
  me: ['auth', 'me'] as const,
  items: (q: string) => ['items', 'list', q] as const,
  item: (id: string) => ['items', 'detail', id] as const,
  itemCatalog: (id: string) => ['items', 'catalog', id] as const,
  itemProfile: (id: string) => ['items', 'profile', id] as const,
  unknownNames: ['catalog', 'unknown-names'] as const,
  linkingIgnored: ['catalog', 'linking-ignored'] as const,
  cloverCategories: ['clover', 'categories'] as const,
  customers: (q: string, stage = '', archived = false) => ['customers', 'list', q, stage, archived] as const,
  vendorBook: (archived: boolean) => ['vendors', 'book', archived] as const,
  activities: (customerId: string) => ['customers', 'activities', customerId] as const,
  followups: (date: string) => ['crm', 'followups', date] as const,
  crmTrends: (date: string) => ['crm', 'trends', date] as const,
  itemUsda: (itemId: string) => ['items', 'usda', itemId] as const,
  forecastAhead: (from: string, to: string) => ['forecast', 'ahead', from, to] as const,
  customer: (id: string) => ['customers', 'detail', id] as const,
  orderLists: ['orders', 'list'] as const,
  orders: (date: string) => ['orders', 'list', date] as const,
  order: (id: string) => ['orders', 'detail', id] as const,
  countSheet: (site: string) => ['stock', 'count-sheet', site] as const,
  sites: ['sites'] as const,
  managedUsers: ['users', 'manage'] as const,
  payables: ['payables'] as const,
  passkeys: ['auth', 'passkeys'] as const,
  lotTrace: (code: string) => ['trace', 'lot', code] as const,
  pattern: (customerId: string) => ['customers', 'pattern', customerId] as const,
  priceSheet: (customerId: string) => ['customers', 'price-sheet', customerId] as const,
  expected: (from: string, to: string) => ['forecast', 'expected', from, to] as const,
  orderDay: (q: string) => ['forecast', 'order-day', q] as const,
  orderDays: ['forecast', 'order-days'] as const,
  vendorSheets: (q: string) => ['forecast', 'vendor-sheets', q] as const,
  vendorShorts: (q: string) => ['forecast', 'vendor-shorts', q] as const,
  orderHistory: (q: string) => ['forecast', 'order-history', q] as const,
  vendorOffers: ['forecast', 'vendor-offers'] as const,
  vendorPrices: (q: string) => ['costs', 'vendor-prices', q] as const,
  vendorOffer: (id: string) => ['forecast', 'vendor-offers', id] as const,
  traceExport: (q: string) => ['trace', 'export', q] as const,
  systemHealth: ['system', 'health'] as const,
  systemJobs: ['system', 'jobs'] as const,
  qboStatus: ['qbo', 'status'] as const,
  qboCheck: ['qbo', 'check'] as const,
  qboMatching: ['qbo', 'matching'] as const,
  qboSwitchMatching: ['qbo', 'matching', 'switch'] as const,
  qboBalances: ['qbo', 'balances'] as const,
  qboMirror: ['qbo', 'mirror'] as const,
  booksSwitch: ['qbo', 'switch'] as const,
  pnl: (from: string, to: string, by: string) => ['qbo', 'pnl', from, to, by] as const,
  qboMirrorCheck: ['qbo', 'mirror', 'check'] as const,
  qboWrite: ['qbo', 'write'] as const,
  practice: ['practice'] as const,
  priceWorksheet: (q: string) => ['pricing', 'worksheet', q] as const,
  priceList: (q: string) => ['pricing', 'list', q] as const,
  companies: ['companies'] as const,
  transfers: (q: string) => ['transfers', q] as const,
  priceTiers: ['pricing', 'tiers'] as const,
  invoice: (id: string) => ['invoices', 'detail', id] as const,
  invoiceAdjustments: (id: string) => ['invoices', 'adjustments', id] as const,
  lostSales: (from: string, to: string) => ['lost-sales', from, to] as const,
  invoices: (q: string) => ['invoices', 'list', q] as const,
  statement: (customerId: string, practice = false) => ['customers', 'statement', customerId, practice ? 'practice' : 'real'] as const,
  emailStatus: ['email', 'status'] as const,
  squareStatus: ['square', 'status'] as const,
  invoiceEmails: (id: string) => ['email', 'invoice', id] as const,
  customerEmails: (id: string) => ['email', 'customer', id] as const,
  publicDocument: (token: string) => ['public', token] as const,
  ledger: ['ledger', 'settings'] as const,
  vendors: ['vendors'] as const,
  vendorCodes: (vendorId: string) => ['vendor-codes', vendorId] as const,
  receipts: (q: string) => ['receipts', 'list', q] as const,
  receipt: (id: string) => ['receipts', 'detail', id] as const,
  lots: (q: string) => ['stock', 'lots', q] as const,
  pickList: (date: string, site: string) => ['stock', 'pick', date, site] as const,
  prep: (from: string, to: string) => ['stock', 'prep', from, to] as const,
  standing: (customerId: string) => ['customers', 'standing', customerId] as const,
  standingDue: (date: string) => ['orders', 'standing-due', date] as const,
  dashboard: ['dashboard'] as const,
  signals: ['reports', 'signals'] as const,
  sales: (from: string, to: string, grain: string) => ['reports', 'sales', from, to, grain] as const,
  workload: (from: string, to: string) => ['reports', 'workload', from, to] as const,
  apiTokens: ['api-tokens'] as const,
  users: ['users'] as const,
  routes: (date: string) => ['routes', date] as const,
  routeDrivers: ['routes', 'drivers'] as const,
  messagingStatus: ['messages', 'status'] as const,
  messageInbox: ['messages', 'inbox'] as const,
  messageThread: (customerId: string) => ['messages', 'thread', customerId] as const,
  audience: (item: string, days: number) => ['messages', 'audience', item, days] as const,
  portalHome: ['portal', 'home'] as const,
  portalCatalog: ['portal', 'catalog'] as const,
  portalDates: ['portal', 'dates'] as const,
  portalOrders: ['portal', 'orders'] as const,
  portalOrder: (id: string) => ['portal', 'orders', id] as const,
  portalStatement: ['portal', 'statement'] as const,
  portalInvoices: ['portal', 'invoices'] as const,
  portalInvoice: (id: string) => ['portal', 'invoices', id] as const,
  portalSettings: ['settings', 'portal'] as const,
  oauthClient: (clientId: string) => ['oauth', 'client', clientId] as const,
  buy: (days: number) => ['buy', days] as const,
  purchaseOrders: ['purchase-orders'] as const,
  readerStatus: ['vendor-docs', 'reader'] as const,
  vendorDocs: (q: string) => ['vendor-docs', 'list', q] as const,
  papers: (status: string, offset = 0) => ['papers', status, offset] as const,
  gmail: ['gmail'] as const,
  vendorDoc: (id: string) => ['vendor-docs', 'detail', id] as const,
  intel: (itemId: string) => ['items', 'intel', itemId] as const,
  priceHistory: (itemId: string, from: string, to: string) => ['items', 'price-history', itemId, from, to] as const,
  competitors: ['competitors'] as const,
  profit: (by: string, from: string, to: string) => ['reports', 'profit', by, from, to] as const,
  stalePrices: (threshold: number) => ['reports', 'stale-prices', threshold] as const,
  priceChanges: (from: string, to: string) => ['reports', 'price-changes', from, to] as const,
  costDash: (from: string, to: string, vendor: string) => ['reports', 'costs-prices', from, to, vendor] as const,
  costDashItem: (id: string, vendor: string, tier: string) => ['reports', 'costs-prices', 'item', id, vendor, tier] as const,
  suggestions: (customerId: string) => ['customers', customerId, 'suggestions'] as const,
  marginSettings: ['settings', 'margin'] as const,
  renewals: ['compliance', 'renewals'] as const,
  weather: ['weather'] as const,
  temperatures: (from: string, to: string) => ['compliance', 'temperatures', from, to] as const,
  foodSafety: ['settings', 'food-safety'] as const,
  vehicles: ['compliance', 'vehicles'] as const,
  vehicleLogs: (id: string) => ['compliance', 'vehicles', id, 'logs'] as const,
  aging: ['reports', 'aging'] as const,
  taxSettings: ['tax', 'settings'] as const,
  pacaSettings: ['paca', 'settings'] as const,
  companySettings: ['settings', 'company'] as const,
  pacaLetter: (customerId: string) => ['paca', 'letter', customerId] as const,
  exceptions: (from: string, to: string) => ['reports', 'exceptions', from, to] as const,
};

/** Reads are safe to repeat: retry transport failures and 5xx, never a 4xx answer. */
function retryQuery(failureCount: number, error: unknown): boolean {
  if (!(error instanceof ApiError)) return failureCount < 2;
  return (error.status === 0 || error.status >= 500) && failureCount < 2;
}

export function createQueryClient(): QueryClient {
  // Any 401 means the session is gone: clearing "me" makes the route guard send the user to /login.
  const onError = (error: unknown) => {
    if (error instanceof ApiError && error.status === 401) client.setQueryData(qk.me, null);
  };
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      // offlineFirst: always try the fetch, so the service worker can answer from its cache.
      queries: { networkMode: 'offlineFirst', staleTime: 30_000, gcTime: 30 * 60_000, retry: retryQuery },
      // online: writes wait while the phone is offline and go out on reconnect.
      mutations: { networkMode: 'online', retry: 0 },
    },
  });
  return client;
}
